import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export type FundingSourceType = 'public' | 'private' | 'bank' | 'internal';
export type FundingCategory = 'operating' | 'investment' | 'equity' | 'loan';
export type FundingStatus = 'planned' | 'promised' | 'signed' | 'in_progress' | 'paid' | 'cancelled';

export interface FundingSource {
  id: string;
  org_id: string;
  name: string;
  source_type: FundingSourceType;
  contact_info?: any;
  created_at: string;
  updated_at: string;
}

export interface FundingCommitment {
  id: string;
  org_id: string;
  source_id: string;
  title: string;
  category: FundingCategory;
  commitment_amount: number;
  convention_start_date?: string;
  convention_end_date?: string;
  allocation?: string;
  status: FundingStatus;
  phase_tag?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface FundingDisbursement {
  id: string;
  commitment_id: string;
  amount: number;
  paid_date: string;
  reference?: string;
  notes?: string;
  created_at: string;
}

export interface FundingLinkToForecast {
  id: string;
  commitment_id: string;
  forecast_line_item_id: string;
  allocation_ratio?: number;
  created_at: string;
}

export interface CommitmentWithDetails extends FundingCommitment {
  source: FundingSource;
  disbursements: FundingDisbursement[];
  paid_total: number;
  remaining: number;
}

export function useFunding() {
  const [sources, setSources] = useState<FundingSource[]>([]);
  const [commitments, setCommitments] = useState<CommitmentWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSources = async () => {
    try {
      const { data, error } = await supabase
        .from('funding_sources')
        .select('*')
        .order('name');

      if (error) throw error;
      setSources(data || []);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadCommitments = async () => {
    try {
      const { data: commitmentsData, error: commitmentsError } = await supabase
        .from('funding_commitments')
        .select('*')
        .order('created_at', { ascending: false });

      if (commitmentsError) throw commitmentsError;

      const { data: sourcesData, error: sourcesError } = await supabase
        .from('funding_sources')
        .select('*');

      if (sourcesError) throw sourcesError;

      const { data: disbursementsData, error: disbursementsError } = await supabase
        .from('funding_disbursements')
        .select('*')
        .order('paid_date', { ascending: false });

      if (disbursementsError) throw disbursementsError;

      const commitmentsWithDetails: CommitmentWithDetails[] = (commitmentsData || []).map(commitment => {
        const source = sourcesData?.find(s => s.id === commitment.source_id);
        const commitmentDisbursements = disbursementsData?.filter(d => d.commitment_id === commitment.id) || [];
        const paid_total = commitmentDisbursements.reduce((sum, d) => sum + parseFloat(d.amount.toString()), 0);
        const remaining = parseFloat(commitment.commitment_amount.toString()) - paid_total;

        return {
          ...commitment,
          source: source as FundingSource,
          disbursements: commitmentDisbursements,
          paid_total,
          remaining
        };
      });

      setCommitments(commitmentsWithDetails);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    await Promise.all([loadSources(), loadCommitments()]);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const createSource = async (source: Omit<FundingSource, 'id' | 'org_id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('funding_sources')
        .insert([{ ...source, org_id: user.id }]);

      if (error) throw error;
      await loadSources();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const updateSource = async (id: string, updates: Partial<FundingSource>) => {
    try {
      const { error } = await supabase
        .from('funding_sources')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await loadSources();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const deleteSource = async (id: string) => {
    try {
      const { error } = await supabase
        .from('funding_sources')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadSources();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const createCommitment = async (commitment: Omit<FundingCommitment, 'id' | 'org_id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('funding_commitments')
        .insert([{ ...commitment, org_id: user.id }]);

      if (error) throw error;
      await loadCommitments();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const updateCommitment = async (id: string, updates: Partial<FundingCommitment>) => {
    try {
      const { error } = await supabase
        .from('funding_commitments')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await loadCommitments();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const deleteCommitment = async (id: string) => {
    try {
      const { error } = await supabase
        .from('funding_commitments')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadCommitments();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const createDisbursement = async (disbursement: Omit<FundingDisbursement, 'id' | 'created_at'>) => {
    try {
      const { error } = await supabase
        .from('funding_disbursements')
        .insert([disbursement]);

      if (error) throw error;
      await loadCommitments();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const updateDisbursement = async (id: string, updates: Partial<FundingDisbursement>) => {
    try {
      const { error } = await supabase
        .from('funding_disbursements')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await loadCommitments();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const deleteDisbursement = async (id: string) => {
    try {
      const { error } = await supabase
        .from('funding_disbursements')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadCommitments();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const getCommitmentsByCategory = (category: FundingCategory, sourceType?: FundingSourceType) => {
    return commitments.filter(c => {
      const matchesCategory = c.category === category;
      const matchesSourceType = !sourceType || c.source?.source_type === sourceType;
      return matchesCategory && matchesSourceType;
    });
  };

  const getTotalsByCategory = () => {
    const totals = {
      operating: { committed: 0, paid: 0, remaining: 0 },
      investment: { committed: 0, paid: 0, remaining: 0 },
      equity: { committed: 0, paid: 0, remaining: 0 },
      loan: { committed: 0, paid: 0, remaining: 0 }
    };

    commitments.forEach(c => {
      totals[c.category].committed += parseFloat(c.commitment_amount.toString());
      totals[c.category].paid += c.paid_total;
      totals[c.category].remaining += c.remaining;
    });

    return totals;
  };

  const getTotalsBySourceType = () => {
    const totals = {
      public: { committed: 0, paid: 0, remaining: 0 },
      private: { committed: 0, paid: 0, remaining: 0 },
      bank: { committed: 0, paid: 0, remaining: 0 },
      internal: { committed: 0, paid: 0, remaining: 0 }
    };

    commitments.forEach(c => {
      if (c.source) {
        const type = c.source.source_type;
        totals[type].committed += parseFloat(c.commitment_amount.toString());
        totals[type].paid += c.paid_total;
        totals[type].remaining += c.remaining;
      }
    });

    return totals;
  };

  return {
    sources,
    commitments,
    loading,
    error,
    createSource,
    updateSource,
    deleteSource,
    createCommitment,
    updateCommitment,
    deleteCommitment,
    createDisbursement,
    updateDisbursement,
    deleteDisbursement,
    getCommitmentsByCategory,
    getTotalsByCategory,
    getTotalsBySourceType,
    refresh: loadData
  };
}
